import type { MutableRefObject } from 'react';
import { useRef, useEffect } from 'react';

/**
 * Используйте setTimeout с хуками декларативно.
 *
 * @see https://stackoverflow.com/a/59274757/3723993
 * @see https://overreacted.io/making-setinterval-declarative-with-react-hooks/
 */
export function useTimeout(
    callback: () => void,
    delay: number | null,
    deps: React.DependencyList = [],
): MutableRefObject<number | null> {
    const timeoutRef = useRef<number | null>(null);
    const callbackRef = useRef(callback);

    // Запоминаем последний обратный вызов:
    // Без этого, если вы измените обратный вызов, когда сработает setTimeout, он
    // все равно вызовет ваш старый обратный вызов.
    // Если вы добавите `callback` в deps useEffect, все будет работать нормально, но
    // таймаут будет сброшен.

    useEffect(() => {
        callbackRef.current = callback;
    }, [callback]);

    // Настройка тайм-аута:
    // eslint-disable-next-line consistent-return
    useEffect(() => {
        if (typeof delay === 'number' && !Number.isNaN(delay)) {
            timeoutRef.current = window.setTimeout(() => callbackRef.current(), delay);

            // Clear timeout if the components is unmounted or the delay changes:
            return () => {
                window.clearTimeout(timeoutRef.current || 0);

                timeoutRef.current = null;
            };
        }

        // Элемент распространения означает, что переданные зависимости
        // не могут быть статически проверены (это нормально):
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [delay, ...deps]);

    // Если вы хотите вручную очистить тайм-аут потребляющего компонента...:
    return timeoutRef;
}
